import { extendTheme, ThemeConfig } from "@chakra-ui/react";

const config: ThemeConfig = {
  initialColorMode: "dark",
  useSystemColorMode: false,
};

const theme = extendTheme({
  config,
  fonts: {
    heading: "Inter, system-ui, sans-serif",
    body: "Inter, system-ui, sans-serif",
  },
  colors: {
    brand: {
      50: "#e3f2fd",
      100: "#bbdefb",
      200: "#90caf9",
      300: "#64b5f6",
      400: "#42a5f5",
      500: "#2196f3",
      600: "#1e88e5",
      700: "#1976d2",
      800: "#1565c0",
      900: "#0d47a1",
    },
    gray: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    purple: {
      50: "#faf5ff",
      100: "#f3e8ff",
      200: "#e9d5ff",
      300: "#d8b4fe",
      400: "#c084fc",
      500: "#a855f7",
      600: "#9333ea",
      700: "#7c3aed",
      800: "#6b21a8",
      900: "#581c87",
    },
    green: {
      50: "#f0fdf4",
      100: "#dcfce7",
      200: "#bbf7d0",
      300: "#86efac",
      400: "#4ade80",
      500: "#22c55e",
      600: "#16a34a",
      700: "#15803d",
      800: "#166534",
      900: "#14532d",
    },
    red: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d",
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: "500",
        borderRadius: "8px",
        _focus: {
          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
        },
      },
      variants: {
        solid: {
          bg: "brand.600",
          color: "white",
          _hover: {
            bg: "brand.700",
            transform: "translateY(-1px)",
            boxShadow: "lg",
          },
          _active: {
            bg: "brand.800",
            transform: "translateY(0)",
          },
        },
        ghost: {
          color: "gray.300",
          _hover: {
            bg: "gray.700",
            color: "white",
          },
          _active: {
            bg: "gray.600",
          },
        },
        outline: {
          borderColor: "gray.600",
          color: "gray.300",
          _hover: {
            bg: "gray.700",
            borderColor: "gray.500",
            color: "white",
          },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: "12px",
          bg: "gray.800",
          border: "1px solid",
          borderColor: "gray.700",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    Input: {
      variants: {
        filled: {
          field: {
            bg: "gray.800",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "gray.600",
            _hover: {
              bg: "gray.700",
              borderColor: "gray.500",
            },
            _focus: {
              bg: "gray.700",
              borderColor: "brand.500",
              boxShadow: "0 0 0 1px #2196f3",
            },
          },
        },
      },
      defaultProps: {
        variant: "filled",
      },
    },
    FormLabel: {
      baseStyle: {
        color: "gray.300",
        fontWeight: "500",
        mb: 2,
      },
    },
    Heading: {
      baseStyle: {
        color: "white",
        fontWeight: "600",
      },
    },
    Text: {
      baseStyle: {
        color: "gray.300",
      },
    },
  },
  styles: {
    global: {
      body: {
        bg: "gray.900",
        color: "gray.300",
        fontFamily: "Inter, system-ui, sans-serif",
      },
      "*::placeholder": {
        color: "gray.500",
      },
      "*, *::before, &::after": {
        borderColor: "gray.700",
      },
    },
  },
});

export default theme;